import { useEffect, useMemo, useState } from "react";
import {
  ResponsiveContainer, PieChart, Pie, Cell, Tooltip, Legend,
  ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid,
  BarChart,
} from "recharts";
import { apiFetch } from "../../api";
import { BASE_URL } from "../../../commonUrls";
import ForecastPanel from "../transactions/ForecastPanel";
import "./SpendingInsights.css";

const MONTHLY_URL   = `${BASE_URL}/spending/monthly`;
const ANALYTICS_URL = `${BASE_URL}/analytics/spending`;

const PIE_COLORS = [
  "#2563eb", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6",
  "#06b6d4", "#ec4899", "#84cc16", "#f97316", "#6366f1",
];

function fmt(n) {
  if (n == null) return "₹0";
  return "₹" + Number(n).toLocaleString("en-IN", { maximumFractionDigits: 0 });
}

function todayStr() {
  return new Date().toISOString().split("T")[0];
}

function defaultFrom() {
  const d = new Date();
  d.setMonth(d.getMonth() - 3);
  d.setDate(1);
  return d.toISOString().split("T")[0];
}

function monthOptions(count = 8) {
  const now = new Date();
  return Array.from({ length: count }, (_, i) => {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    return { value, label: d.toLocaleDateString(undefined, { month: "long", year: "numeric" }) };
  });
}

function AmountTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="si-tooltip">
      <div className="si-tooltip-label">{label}</div>
      {payload.map((p) => (
        <div key={p.name} style={{ color: p.color }}>{p.name}: {fmt(p.value)}</div>
      ))}
    </div>
  );
}

function PieTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const d = payload[0];
  return (
    <div className="si-tooltip">
      <div className="si-tooltip-label">{d.name}</div>
      <div>{fmt(d.value)} ({d.payload.percentage}%)</div>
    </div>
  );
}

// ── Root ─────────────────────────────────────────────────────────────────────

export default function SpendingInsights() {
  const [activeTab, setActiveTab] = useState("analytics");

  return (
    <div className="spending-page">
      <ForecastPanel />
      <div className="si-tabs">
        <button className={activeTab === "analytics" ? "si-tab active" : "si-tab"}
          onClick={() => setActiveTab("analytics")}>Analytics</button>
        <button className={activeTab === "monthly" ? "si-tab active" : "si-tab"}
          onClick={() => setActiveTab("monthly")}>Monthly Detail</button>
      </div>
      {activeTab === "analytics" ? <AnalyticsView /> : <MonthlyView />}
    </div>
  );
}

// ── Analytics tab ─────────────────────────────────────────────────────────────

function AnalyticsView() {
  const [from, setFrom] = useState(defaultFrom);
  const [to, setTo]     = useState(todayStr);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState("");

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError("");
    apiFetch(`${ANALYTICS_URL}?from=${from}&to=${to}&merchantLimit=10`)
      .then((res) => (res.ok ? res.json() : Promise.reject(res.statusText)))
      .then((d) => { if (alive) setData(d); })
      .catch((e) => { if (alive) setError("Failed to load analytics: " + e); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [from, to]);

  const pieData = useMemo(() =>
    (data?.categoryBreakdown || []).map((c) => ({
      name: c.categoryName,
      value: Number(c.amount),
      percentage: c.percentage,
    })), [data]);

  const trendData = useMemo(() =>
    (data?.monthlyTrend || []).map((m) => ({
      month: m.month,
      Income: Number(m.income),
      Expense: Number(m.expense),
      Net: Number(m.net),
    })), [data]);

  const merchantData = useMemo(() =>
    (data?.merchantBreakdown || []).map((m) => ({
      name: m.nickname || m.merchantName || m.merchantId,
      amount: Number(m.amount),
      count: m.count,
    })), [data]);

  return (
    <>
      <header className="spending-header">
        <div>
          <h1>Spending Analytics</h1>
          <p>Category, trend, and merchant breakdown across a date range.</p>
        </div>
        <div className="si-date-range">
          <label>From <input type="date" value={from} max={to}
            onChange={(e) => setFrom(e.target.value)} /></label>
          <label>To <input type="date" value={to} min={from} max={todayStr()}
            onChange={(e) => setTo(e.target.value)} /></label>
        </div>
      </header>

      {loading ? (
        <p className="spending-muted">Loading…</p>
      ) : error ? (
        <p className="spending-muted">{error}</p>
      ) : data ? (
        <>
          <div className="spending-metrics">
            <Metric label="Total spent"  value={fmt(data.totalExpense)} />
            <Metric label="Total income" value={fmt(data.totalIncome)} />
            <Metric label="Net cashflow" value={fmt(data.netCashflow)}
              accent={Number(data.netCashflow) >= 0 ? "green" : "red"} />
            <Metric label="Savings rate" value={`${data.savingsRate}%`}
              accent={data.savingsRate >= 20 ? "green" : data.savingsRate >= 0 ? "amber" : "red"} />
          </div>

          <div className="si-charts-grid">
            {/* Category donut */}
            <section className="spending-card">
              <h2>Category breakdown</h2>
              {pieData.length === 0 ? (
                <p className="spending-muted">No expense data.</p>
              ) : (
                <ResponsiveContainer width="100%" height={280}>
                  <PieChart>
                    <Pie data={pieData} dataKey="value" nameKey="name"
                      cx="50%" cy="50%" outerRadius={90} innerRadius={44} paddingAngle={2}>
                      {pieData.map((_, i) => (
                        <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip content={<PieTooltip />} />
                    <Legend iconType="circle" iconSize={10} />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </section>

            {/* Monthly combo chart */}
            <section className="spending-card">
              <h2>Monthly income vs expense</h2>
              {trendData.length === 0 ? (
                <p className="spending-muted">No data.</p>
              ) : (
                <ResponsiveContainer width="100%" height={280}>
                  <ComposedChart data={trendData} margin={{ top: 4, right: 16, bottom: 0, left: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }}
                      tickFormatter={(v) => v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v} />
                    <Tooltip content={<AmountTooltip />} />
                    <Legend iconSize={10} />
                    <Bar dataKey="Income" fill="#10b981" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="Expense" fill="#ef4444" radius={[4, 4, 0, 0]} />
                    <Line type="monotone" dataKey="Net" stroke="#2563eb"
                      strokeWidth={2} dot={false} />
                  </ComposedChart>
                </ResponsiveContainer>
              )}
            </section>

            {/* Merchant horizontal bar */}
            <section className="spending-card si-merchant-card">
              <h2>Top merchants by spend</h2>
              {merchantData.length === 0 ? (
                <p className="spending-muted">No merchant data.</p>
              ) : (
                <ResponsiveContainer width="100%"
                  height={Math.max(200, merchantData.length * 36)}>
                  <BarChart layout="vertical" data={merchantData}
                    margin={{ top: 4, right: 64, bottom: 0, left: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" horizontal={false} />
                    <XAxis type="number" tick={{ fontSize: 11 }}
                      tickFormatter={(v) => v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v} />
                    <YAxis type="category" dataKey="name" width={120} tick={{ fontSize: 11 }} />
                    <Tooltip
                      formatter={(v, _n, p) => [fmt(v) + " · " + p.payload.count + " txn(s)", "Spend"]} />
                    <Bar dataKey="amount" fill="#8b5cf6" radius={[0, 4, 4, 0]}
                      label={{ position: "right", fontSize: 11, formatter: (v) => fmt(v) }} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </section>

            {/* Auto-generated text insights */}
            {data.insights?.length > 0 && (
              <section className="spending-card si-insights-card">
                <h2>Insights</h2>
                <ul className="si-insights-list">
                  {data.insights.map((txt, i) => <li key={i}>{txt}</li>)}
                </ul>
              </section>
            )}
          </div>
        </>
      ) : null}
    </>
  );
}

// ── Monthly tab (existing per-month view) ─────────────────────────────────────

function MonthlyView() {
  const options = useMemo(() => monthOptions(8), []);
  const [month, setMonth] = useState(options[0]?.value);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError("");
    apiFetch(`${MONTHLY_URL}?month=${month}`)
      .then((res) => (res.ok ? res.json() : Promise.reject()))
      .then((d) => { if (alive) setSummary(d); })
      .catch(() => { if (alive) setError("Unable to load spending insights."); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [month]);

  return (
    <>
      <header className="spending-header">
        <div>
          <h1>Monthly Detail</h1>
          <p>Category breakdown and daily trend for the selected month.</p>
        </div>
        <select value={month} onChange={(e) => setMonth(e.target.value)}>
          {options.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      </header>

      {loading ? (
        <p className="spending-muted">Loading insights…</p>
      ) : error ? (
        <p className="spending-muted">{error}</p>
      ) : summary ? (
        <>
          <div className="spending-metrics">
            <Metric label="Total spent"  value={fmt(summary.totalSpent)} />
            <Metric label="Total income" value={fmt(summary.totalIncome)} />
            <Metric label="Net cashflow" value={fmt(summary.netCashflow)}
              accent={Number(summary.netCashflow) >= 0 ? "green" : "red"} />
          </div>
          <div className="spending-grid">
            <section className="spending-card">
              <h2>Category breakdown</h2>
              <CategoryBars data={summary.categoryBreakdown || []} />
            </section>
            <section className="spending-card">
              <h2>Daily trend</h2>
              <DailyTrendChart data={summary.dailyTrend || []} />
            </section>
          </div>
        </>
      ) : null}
    </>
  );
}

// ── Shared sub-components ─────────────────────────────────────────────────────

function Metric({ label, value, accent }) {
  return (
    <div className={`spending-metric${accent ? " accent-" + accent : ""}`}>
      <div className="spending-metric-label">{label}</div>
      <div className="spending-metric-value">{value}</div>
    </div>
  );
}

function CategoryBars({ data }) {
  if (!data.length) return <p className="spending-muted">No spend recorded.</p>;
  const max = Math.max(...data.map((d) => Number(d.amount || 0)), 1);
  return (
    <div className="category-chart">
      {data.slice(0, 8).map((item) => (
        <div key={item.categoryName} className="category-row">
          <div className="category-label">{item.categoryName}</div>
          <div className="category-bar">
            <span style={{ width: `${(Number(item.amount || 0) / max) * 100}%` }} />
          </div>
          <div className="category-value">{fmt(item.amount)}</div>
        </div>
      ))}
    </div>
  );
}

function DailyTrendChart({ data }) {
  if (!data.length) return <p className="spending-muted">No spend recorded.</p>;
  const w = 600, h = 200, p = 24;
  const values = data.map((d) => Number(d.amount || 0));
  const max = Math.max(...values, 1);
  const points = values.map((v, i) => {
    const x = p + (i / Math.max(1, values.length - 1)) * (w - p * 2);
    const y = h - p - (v / max) * (h - p * 2);
    return `${x},${y}`;
  });
  return (
    <svg className="trend-chart" viewBox={`0 0 ${w} ${h}`} role="img" aria-label="Daily spend trend">
      <polyline points={points.join(" ")} fill="none" stroke="#2563eb" strokeWidth="2" />
      <line x1={p} y1={h - p} x2={w - p} y2={h - p} stroke="#e2e8f0" />
    </svg>
  );
}
